import Button from "@/components/ui/button";
import useNotification from "@/hooks/use-notification";
import styles from "../styles/login.module.scss";
import { authApi } from "../services/auth.api";
import { setUserFromToken } from "@/store";
import { OTPVerificationProps } from "@/types/general";
import { t } from "i18next";
import React, { useState } from "react";
import { useOTPInput } from "../hooks/useOTPInput";
import { useOTPTimer } from "../hooks/useOTPTimer";
import BackButton from "./BackButton";

interface OTPVerificationComponentProps {
  onVerificationSuccess: (tokenPayload: any) => void;
  onBack?: () => void;
  identifier?: string;
  domainID?: string;
  verificationType?: "login" | "forgot-password";
  phoneNumber?: string;
}

const OTPVerification: React.FC<
  OTPVerificationComponentProps & OTPVerificationProps
> = ({
  onVerificationSuccess,
  onBack,
  identifier,
  domainID,
  verificationType = "login",
}) => {
  const { notifyError, notifySuccess } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const {
    otpValues,
    inputRefs,
    handleChange,
    handleKeyDown,
    handlePaste,
    getOtpString,
  } = useOTPInput();
  const { timer, canResend, resetTimer } = useOTPTimer();

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const otpString = getOtpString();

    if (otpString.length !== 6) {
      notifyError(t("login.pleaseEnterValidOtp"));
      return;
    }

    if (verificationType === "forgot-password") {
      // Şifre sıfırlama: OTP ayrı doğrulanmaz, code CreateNewPassword'da resetPassword API ile gönderilir
      onVerificationSuccess({ otpCode: otpString });
      return;
    }

    setIsLoading(true);

    try {
      const response = await authApi.verifyOTP({
        domainID: domainID || "",
        identifier: identifier || "",
        otpCode: otpString,
      });
      setUserFromToken(response, false);
      notifySuccess(t("login.otpVerified"));
      onVerificationSuccess(response);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || t("login.otpFailed");
      notifyError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (!canResend || isResending) return;

    setIsResending(true);

    try {
      if (verificationType === "forgot-password") {
        // Şifre sıfırlama OTP: email ile forgot-password tekrar çağrılır (identifier = email)
        await authApi.forgotPassword({ email: identifier || "" });
      } else {
        await authApi.resendOTP({
          phone: identifier || "",
          domainID: domainID || "",
        });
      }
      resetTimer();
      notifySuccess(t("login.otpResent"));
    } catch (err: any) {
      notifyError(t("login.otpResendFailed"));
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className={styles["otp-verification"]}>
      {onBack && (
        <div className={styles["otp-header-back"]}>
          <BackButton onClick={onBack} />
        </div>
      )}

      <div className={styles["otp-header"]}>
        <h2>{t("login.otpTitle")}</h2>
        <p>{t("login.otpDescription")}</p>
      </div>

      <form onSubmit={handleOtpSubmit} className={styles["otp-form"]}>
        <div className={styles["otp-input-container"]}>
          <div className={styles["otp-inputs"]}>
            {otpValues.map((value, index) => (
              <input
                autoComplete="off"
                key={index}
                maxLength={1}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                ref={(ref) => (inputRefs.current[index] = ref)}
                type="text"
                value={value}
                className={`${styles["otp-input"]} ${value ? styles["otp-input-filled"] : ""}`}
              />
            ))}
          </div>
        </div>

        <Button
          color="primary"
          size="medium"
          style={{ marginTop: "2rem", width: "100%" }}
          type="submit"
          variant="contained"
          disabled={isLoading}
        >
          {isLoading ? t("common.loading") : t("login.verify")}
        </Button>
      </form>

      <div className={styles["otp-footer"]}>
        {!canResend && timer > 0 && (
          <p className={styles["timer"]}>
            <span style={{ color: "#e7212b" }}>{timer}s</span>{" "}
            {t("login.canResendAfter")}
          </p>
        )}

        <Button
          color="primary"
          size="medium"
          style={{ marginTop: "1rem", width: "100%" }}
          type="button"
          onClick={handleResendOTP}
          className={styles["resend-button"]}
          disabled={!canResend || isResending}
          variant="outlined"
        >
          {isResending ? t("common.loading") : t("login.resendCode")}
        </Button>
      </div>
    </div>
  );
};

export default OTPVerification;
